import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

/** Map a plain-text offset (TipTap getText with \\n separators) to a PM position. */
export function plainOffsetToPos(
  doc: ProseMirrorNode,
  plainOffset: number,
): number | null {
  let plain = 0;
  let blockIndex = 0;
  let found: number | null = null;

  doc.forEach((block, blockPos) => {
    if (found !== null) return;
    if (blockIndex > 0) {
      if (plainOffset === plain) {
        found = blockPos;
        return;
      }
      plain += 1;
    }
    blockIndex += 1;
    if (!block.isTextblock) return;

    let inner = 0;
    block.forEach((child, childOffset) => {
      if (found !== null) return;
      if (!child.isText || !child.text) {
        inner += child.nodeSize;
        return;
      }
      const len = child.text.length;
      if (plainOffset <= plain + len) {
        found = blockPos + 1 + childOffset + (plainOffset - plain);
        return;
      }
      plain += len;
      inner += child.nodeSize;
    });

    if (found === null && plainOffset === plain) {
      found = blockPos + 1 + inner;
    }
  });

  return found;
}

/** Map a PM position to a plain-text offset. */
export function posToPlainOffset(
  doc: ProseMirrorNode,
  pos: number,
): number | null {
  let plain = 0;
  let blockIndex = 0;
  let result: number | null = null;

  doc.forEach((block, blockPos) => {
    if (result !== null) return;
    if (blockIndex > 0) {
      if (pos <= blockPos) {
        result = plain;
        return;
      }
      plain += 1;
    }
    blockIndex += 1;

    if (pos <= blockPos) {
      result = plain;
      return;
    }

    block.forEach((child, childOffset) => {
      if (result !== null) return;
      if (!child.isText || !child.text) return;
      const pmFrom = blockPos + 1 + childOffset;
      const pmTo = pmFrom + child.text.length;
      if (pos <= pmTo) {
        result = plain + Math.max(0, pos - pmFrom);
        return;
      }
      plain += child.text.length;
    });

    if (result === null && pos <= blockPos + block.nodeSize) {
      result = plain;
    }
  });

  return result;
}

export function resolveCommentRange(
  content: string,
  from: number | undefined,
  to: number | undefined,
  quote: string | undefined,
): { from: number; to: number } | null {
  if (
    from === undefined ||
    to === undefined ||
    to <= from ||
    from < 0 ||
    to > content.length
  ) {
    if (quote) {
      const idx = content.indexOf(quote);
      if (idx >= 0) return { from: idx, to: idx + quote.length };
    }
    return null;
  }
  const slice = content.slice(from, to);
  if (quote && slice !== quote) {
    const idx = content.indexOf(quote);
    if (idx >= 0) return { from: idx, to: idx + quote.length };
    return null;
  }
  return { from, to };
}

/** Convert a ProseMirror selection to plain-text offsets. */
export function selectionToPlainOffsets(
  doc: ProseMirrorNode,
  from: number,
  to: number,
): { from: number; to: number; quote: string } | null {
  if (to <= from) return null;
  const quote = doc.textBetween(from, to, "\n", "");
  if (!quote) return null;
  const plainFrom = posToPlainOffset(doc, from);
  const plainTo = posToPlainOffset(doc, to);
  if (plainFrom === null || plainTo === null || plainTo <= plainFrom) {
    return null;
  }
  return { from: plainFrom, to: plainTo, quote };
}
